import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./pricing.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import { getPlanPrice, getPlanUnits, hasDiscountNonProfits, isFreeListing, isFreemiumListing, isFreeToInstallListing, isPaidListing, isPaidWithCheckout } from '~/modules/helpers/pricingHelper';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { routerStore, selectScrollToQueryParam } from '~/modules/stores/routerStore';
import { scrollToElement } from '~/modules/utilities/browser';
import { buildFrequencyLabels } from '~/modules/utilities/pricingUtilities';
const SHOW_MORE_SYMBOL = 'chevronright';
const SHOW_LESS_SYMBOL = 'chevrondown';
const DEFAULT_NUMBER_OF_PLANS_VISIBLE = 4;
const SFID_REGEX = new RegExp('^(a|A)0(y|Y)[a-zA-Z0-9]{12}|^(a|A)0(y|Y)[a-zA-Z0-9]{15}');
class Pricing extends LightningElement {
  constructor(...args) {
    super(...args);
    this.pricing = void 0;
    this.
    // @ts-expect-error * Automated Type Error Fix
    scrollToParam = void 0;
    this._toggleShowMoreOrLess = true;
  }
  i18nCallback(i18n) {
    // TODO - I18N: Populate labels here

    this.staticLabels = i18n.listingDetails.pricing;
    this.frequencyLabels = buildFrequencyLabels(this.staticLabels);
  }
  renderedCallback() {
    scrollToElement(this, this.scrollToParam);
  }
  // Set to true when displaying Show More plans in the UI and false when displaying Show Less plans

  // PRICING DATA FROM OAF
  get hasSubSectionHeader() {
    return isFreeListing(this.pricing) || isFreeToInstallListing(this.pricing) || isFreemiumListing(this.pricing);
  }
  get subSectionHeader() {
    if (isFreeListing(this.pricing)) {
      return this.staticLabels.freeSubSectionHeader;
    }
    if (isFreemiumListing(this.pricing)) {
      return this.staticLabels.freemiumSubSectionHeader;
    }
    if (isFreeToInstallListing(this.pricing)) {
      return this.staticLabels.freeToInstallSubSectionHeader;
    }
    return '';
  }
  get paidModelPlans() {
    return this.pricing?.model?.plans ?? [];
  }
  get plans() {
    const displayCheckoutPlanName = isPaidWithCheckout(this.pricing) && this.paidModelPlans?.length > 1;
    const plans = [...this.paidModelPlans];
    return plans.sort((a, b) => a.index - b.index).map((plan, index) => ({
      price: plan.price,
      name: this.getPlanName(plan.plan_name, index + 1, displayCheckoutPlanName, isPaidWithCheckout(this.pricing),
      // @ts-expect-error * Automated Type Error Fix
      this.pricing?.model?.display_plan_names, this.paidModelPlans),
      details: i18nStore.getState().i18n.listingDetails.pricing.paidPlanDetails({
        PRICE: getPlanPrice(plan.currency_code, plan.price),
        CURRENCY_CODE: plan.currency_code,
        UNITS: getPlanUnits(plan),
        FREQUENCY: this.getPlanFrequencyLabel(plan.frequency)
      }),
      isStartingPrice: plan.lowest_starting_price,
      hasTrialAvailable: plan.trial_days != null && plan.trial_days > 0,
      trialPeriodDaysText: plan.trial_days != null ? this.getTrialPeriodDescriptionLabel(String(plan.trial_days)) : '',
      index: plan.index,
      isVisible: index < DEFAULT_NUMBER_OF_PLANS_VISIBLE || !this._toggleShowMoreOrLess
    }));
  }
  get isShowMoreVisible() {
    return this.paidModelPlans?.length > DEFAULT_NUMBER_OF_PLANS_VISIBLE;
  }
  get showMoreOrLessText() {
    return this._toggleShowMoreOrLess ? this.staticLabels.showMore : this.staticLabels.showLess;
  }
  get showMoreOrLessSymbol() {
    return this._toggleShowMoreOrLess ? SHOW_MORE_SYMBOL : SHOW_LESS_SYMBOL;
  }
  handleShowMore() {
    this._toggleShowMoreOrLess = !this._toggleShowMoreOrLess;
  }
  get pricingCustomDetailsDescription() {
    // @ts-expect-error * Automated Type Error Fix
    const pricingModel = this.pricing?.model;
    if (isPaidListing(this.pricing)) {
      const model = pricingModel;
      const additionalDetails = model.additional_details ? model.additional_details : '';
      return additionalDetails;
    } else if (isFreeToInstallListing(this.pricing)) {
      const model = pricingModel;
      const additionalDetails = model.additional_details ? model.additional_details : '';
      return additionalDetails;
    } else if (isFreemiumListing(this.pricing)) {
      const model = pricingModel;
      const additionalDetails = model.additional_details ? model.additional_details : '';
      return additionalDetails;
    }
    return '';
  }
  get pricingDetailsDescription() {
    if (isPaidListing(this.pricing)) {
      return (
        // W-11851893 - temporary remove pricing descriptions
        //this.staticLabels.detailsDescriptionRequiresPayment +
        hasDiscountNonProfits(this.pricing) ? this.staticLabels.detailsDescriptionNonProfitsAndPayment : ''
      );
    }
    // W-11851893 - temporary remove pricing descriptions
    // } else if (isFreeToInstallListing(this.pricing)) {
    //     return this.staticLabels.detailsDescriptionFreeToInstall;
    // } else if (isFreemiumListing(this.pricing)) {
    //     return this.staticLabels.detailsDescriptionFreemium;
    // } // On Free pricing model type we display only label text
    // return this.staticLabels.detailsDescriptionFree;
    if (isFreemiumListing(this.pricing)) {
      return hasDiscountNonProfits(this.pricing) ? this.staticLabels.nonProfitDetailsDescriptionFreemium : this.staticLabels.detailsDescriptionFreemium;
    }
    if (isFreeToInstallListing(this.pricing)) {
      return hasDiscountNonProfits(this.pricing) ? this.staticLabels.nonProfitDetailsDescriptionFreeToInstall : this.staticLabels.detailsDescriptionFreeToInstall;
    }
    if (isFreeListing(this.pricing)) {
      return this.staticLabels.detailsDescriptionFree;
    }
    return '';
  }
  get pricingRequirementsAndLimitation() {
    if (isFreemiumListing(this.pricing)) {
      return this.pricing.model.user_limitations ?? '';
    }
    if (isFreeToInstallListing(this.pricing)) {
      return this.pricing.model.additional_services ?? '';
    }
    return '';
  }
  getTrialPeriodDescriptionLabel(period) {
    return i18nStore.getState().i18n.listingDetails.pricing.trialPeriodDescription({
      TRIAL_PERIOD: period
    });
  }
  getPlanFrequencyLabel(frequency) {
    // @ts-expect-error * Automated Type Error Fix
    return this.frequencyLabels[frequency] ?? '';
  }

  /**
   * Used to display the plan name based on the input parameters.
   * @param name - Plan name coming from oaf
   * @param index - index from oaf plus one
   * @param displayCheckoutPlanName - true when pricing is paid with checkout and has more than one plan to display: Plan 1, Plan 2, ... , Plan n
   * @param paidWithCheckout - true when pricing is paid with checkout, if true will also display 'Default Plan' when plan has no name assigned or is checkout with single plan
   * @returns - Plan Name | Default Plan n | Plan n
   */
  getPlanName(name, index, displayCheckoutPlanName, paidWithCheckout, displayCustomPlanNames, plans) {
    const pricing = i18nStore.getState().i18n.listingDetails.pricing;
    // check if the name matches to a salesforce record Id
    if (!displayCheckoutPlanName) {
      if (SFID_REGEX.test(name) || paidWithCheckout) {
        if (plans.length > 1) {
          return pricing.defaultPlanNames({
            VALUE: index
          });
        }
        return pricing.defaultPlanName();
      }
      return name || pricing.defaultPlanName();
    }
    if (displayCheckoutPlanName) {
      if (displayCustomPlanNames) {
        return name;
      }
      return pricing.checkoutPlanName({
        VALUE: index
      });
    }
    if (!displayCustomPlanNames) {
      return pricing.defaultPlan({
        COUNT: plans.length,
        VALUE: index
      });
    }
    return name;
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Pricing, {
  publicProps: {
    pricing: {
      config: 0
    }
  },
  wire: {
    i18nCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    },
    scrollToParam: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: routerStore,
          selector: selectScrollToQueryParam
        };
      }
    }
  },
  fields: ["_toggleShowMoreOrLess"]
});
const __lwc_component_class_internal = _registerComponent(Pricing, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;